// src/wiring/manifest/index.ts
//
// Canonical manifest registry (v1)
// --------------------------------
// Deterministic list of capability manifests.
// Keep ordering stable for reproducible checksums.
//
// Rules:
// - Ordering MUST remain stable across builds for checksum reproducibility.
// - Never mutate the exported list; always return a copy.
// - IDs MUST be unique (DEV assert).
//

import type { CapabilityManifest } from "@/contracts/capabilities/CapabilityManifest";

// Public / witness-safe
import { OmniCastSurfaceWitnessPublicManifest } from "@/wiring/manifest/omnicastSurfaceWitness.public";

// Creator
import { OmniCineAudioResolverCreatorManifest } from "@/wiring/manifest/omnicineAudioResolver.creator";
import { OmniCineExportDownloaderCreatorManifest } from "@/wiring/manifest/omnicineExportDownloader.creator";
import { createGoalBuddyAtomicRunsRuntimeManifest } from "@/wiring/manifest/goalbuddyAtomicRunsRuntime.creator";

// Enterprise (optional)
import { AuditTelemetryPackEnterpriseManifest } from "@/wiring/manifest/auditTelemetryPack.enterprise";

/**
 * Canonical manifest list (stable order).
 * IMPORTANT: edit order only intentionally; it affects checksums.
 */
const MANIFESTS: ReadonlyArray<CapabilityManifest> = Object.freeze([
  // Witness-safe base visibility
  OmniCastSurfaceWitnessPublicManifest,

  // Creator tier: export pipeline
  OmniCineAudioResolverCreatorManifest,
  OmniCineExportDownloaderCreatorManifest,

  // Enterprise tier: audit/telemetry pack (gated by installer)
  AuditTelemetryPackEnterpriseManifest,
]);

function assertUniqueIds(list: ReadonlyArray<CapabilityManifest>): void {
  const seen = new Set<string>();
  const dupes: string[] = [];

  for (const m of list) {
    const id = String(m?.id ?? "").trim();
    if (!id) {
      dupes.push("<empty-id>");
      continue;
    }
    if (seen.has(id)) dupes.push(id);
    seen.add(id);
  }

  if (dupes.length) {
    throw new Error(`[wiring/manifest] Duplicate or invalid capability ids: ${dupes.join(", ")}`);
  }
}

/**
 * Returns the full list used by the installer.
 * NOTE: We append creator manifests produced by factories here (still deterministic).
 */
export function getAllManifests(): CapabilityManifest[] {
  const list: CapabilityManifest[] = [
    ...MANIFESTS,
    // Factory-produced manifest (kept last to preserve checksum stability above)
    createGoalBuddyAtomicRunsRuntimeManifest(),
  ];

  if (import.meta.env.DEV) {
    assertUniqueIds(list);
  }

  // Return a copy to prevent accidental mutation of canonical ordering.
  return list.slice();
}
